import { getActionType, getDashboardLink, getOauthRedirectUrlWithCode, getPostInstallRedirectAction } from '../utils/postInstallation';
import { useHandleBusinessFirst } from './useHandleBusinessFirst';
import { useInstallerContext } from '../context/InstallerContext';
import { useNavigate } from './useNavigate';
import { getInstalledApp } from '../utils/provision';
import { reportPostInstallation, reportRedirectUrlEnd, reportRedirectUrlStart } from '../bi/bi';
import { useModals } from '../context/ModalsContext';
import { ActionType } from '@wix/ambassador-devcenter-site-apps-v1-app/types';
import { usePanorama } from '../context/PanoramaContext';
import isEmpty from 'lodash/isEmpty';
import { validatePostMessageInstallStatus } from '../utils/oauth-validations';
import { useEffect } from 'react';
import { useShouldOpenNewWindow } from './useShouldOpenNewWindow';
import { useNewWindowHandlers } from '../context/NewWindowHandlersContext';
export const useHandlePostInstallation = () => {
  const {
    modals
  } = useModals();
  const {
    updateWindowSrcWithUrl,
    closeNewWindow,
    navigateToSecondRedirectUrlInOauth,
    navigateToPostInstallationUrlInOauth,
    avoidPostInstallationPostMessageInOauth
  } = useNewWindowHandlers();
  const {
    panoramaLogger
  } = usePanorama();
  const {
    appId,
    overrideNavigate,
    avoidPostInstallation,
    tenantId,
    preInstallationData: {
      isExternalDashboard,
      isAppAlreadyInstalled
    },
    version
  } = useInstallerContext();
  const shouldOpenNewWindow = useShouldOpenNewWindow();
  useEffect(() => {
    // for cleanup and not making clutter of event listeners on multiple usage of installer
    if (window.__HANDLE_OAUTH_POST_INSTALL__) {
      window.removeEventListener('message', window.__HANDLE_OAUTH_POST_INSTALL__);
      delete window.__HANDLE_OAUTH_POST_INSTALL__;
    }
  }, []);
  const navigate = useNavigate();
  const fetchBusinessFirstLink = useHandleBusinessFirst();
  const avoidPostInstallInNewWindow = () => {
    closeNewWindow();
  };
  const skipBusinessFirstCheck = (overrideNavigate == null ? void 0 : overrideNavigate.skipBusinessFirstCheck) || version === 'dev';
  return async (provisionSuccessData, isNewWindowOpened) => {
    var _getInstalledApp;
    const requiredActions = ((_getInstalledApp = getInstalledApp(appId, provisionSuccessData)) == null ? void 0 : _getInstalledApp.requiredActions) || [];
    const requiredActionsWithoutOauth = requiredActions.filter(requiredAction => requiredAction.actionType !== ActionType.OAUTH2);
    const postInstallRequiredAction = getPostInstallRedirectAction(requiredActionsWithoutOauth, tenantId);
    const postInstallNavigate = function (_temp) {
      let {
        isAdvancedOauthPostInstall
      } = _temp === void 0 ? {
        isAdvancedOauthPostInstall: false
      } : _temp;
      return navigate(postInstallRequiredAction, isNewWindowOpened, isAdvancedOauthPostInstall ? navigateToPostInstallationUrlInOauth : updateWindowSrcWithUrl, avoidPostInstallInNewWindow, {
        appId,
        version: provisionSuccessData.installedApps[0].version
      });
    };
    let businessFirstUrl = '';
    const getBusinessFirstLink = async () => {
      businessFirstUrl = (await fetchBusinessFirstLink(requiredActionsWithoutOauth)) || '';
      return businessFirstUrl;
    };
    const oauthRedirectLinkWithCode = getOauthRedirectUrlWithCode(requiredActions);
    if (oauthRedirectLinkWithCode) {
      reportRedirectUrlStart({
        url: oauthRedirectLinkWithCode
      });
      navigateToSecondRedirectUrlInOauth(oauthRedirectLinkWithCode);
      const handlePostInstallInOauth = event => {
        if (validatePostMessageInstallStatus(event)) {
          reportRedirectUrlEnd({
            url: oauthRedirectLinkWithCode
          });
          if (avoidPostInstallation) {
            avoidPostInstallationPostMessageInOauth();
          } else if (overrideNavigate != null && overrideNavigate.overrideOauthPostInstallRedirect) {
            postInstallNavigate({
              isAdvancedOauthPostInstall: true
            });
          } else if (postInstallRequiredAction.link) {
            navigateToPostInstallationUrlInOauth(postInstallRequiredAction.link || '');
            reportPostInstallation({
              destination: postInstallRequiredAction.link || ''
            });
          } else {
            avoidPostInstallationPostMessageInOauth();
          }
        }
      };
      // case importify - if the app has external dashboard but still uses /close-window, keep an event listener for it
      window.addEventListener('message', handlePostInstallInOauth);
      window.__HANDLE_OAUTH_POST_INSTALL__ = handlePostInstallInOauth;
      return;
    }
    if (avoidPostInstallation) {
      avoidPostInstallInNewWindow();
      return;
    }
    if (isExternalDashboard) {
      const externalDashboardLink = getDashboardLink(requiredActions);
      if (externalDashboardLink) {
        reportPostInstallation({
          destination: externalDashboardLink
        });
        updateWindowSrcWithUrl(externalDashboardLink);
      }
      return;
    }
    if (!skipBusinessFirstCheck && (await getBusinessFirstLink())) {
      avoidPostInstallInNewWindow();
      reportPostInstallation({
        destination: businessFirstUrl
      });
      const hasDashboard = Boolean(getActionType(ActionType.DASHBOARD, requiredActionsWithoutOauth));
      await modals.businessFirst.open({
        businessFirstUrl,
        postInstallNavigate,
        hasDashboard
      });
      return;
    }
    if (!shouldOpenNewWindow && isAppAlreadyInstalled) {
      const {
        avoidPostInstallationRedirect
      } = await modals.updateAppSuccess.open();
      if (avoidPostInstallationRedirect) {
        return;
      }
    }
    if (isEmpty(requiredActionsWithoutOauth)) {
      panoramaLogger('No required actions to handle (requiredActionsWithoutOauth)');
    }
    if (postInstallRequiredAction.link) {
      reportPostInstallation({
        destination: postInstallRequiredAction.link
      });
      postInstallNavigate();
    }
  };
};
//# sourceMappingURL=useHandlePostInstallation.js.map